import { FolderOutlined, PictureOutlined, SmileOutlined } from '@ant-design/icons'
import { Button, Input, InputRef, Space, Typography } from 'antd'
import { ASSET_TYPE, useCreateAssetMutation } from 'api/assetApi'
import React, { useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { useNavigate, useParams } from 'react-router-dom'
import Utils from 'utils'
import { v4 as uuid } from 'uuid'
import PageBuilder from '../../components/panel/PageBuilder'
import { SendAssetType } from '../../types/Assets'

const buttonWidthStyle = {
    width: 172
}

type TSelection = 'folder' | 'scene' | 'model' | 'material' | 'image' | 'vector' | 'composite'

const FILE_SELECTOR_TYPE: Record<TSelection, string> = {
    folder: '*',
    scene: '*',
    model: '.glb',
    material: '*',
    image: 'image/jpeg, image/png, image/webp',
    vector: '*',
    composite: '*'
}

export type ValueObjectType<T> = {
    [K in keyof T]: T[K]
}[keyof T]

const extRegexp = new RegExp(/\.[^/\\.]+$/)

const CreateAsset: React.FC = () => {
    const [assetName, setAssetName] = useState('')
    const [assetExt, setAssetExt] = useState('')
    const fileSelector = useRef<HTMLInputElement>(null)
    const [selectedFile, setSelectedFile] = useState<File | null>(null)
    const [savingAsset, setSavingAsset] = useState(false)
    const [selectedType, setSelectedType] = useState('')

    const [createAsset] = useCreateAssetMutation()

    const navigate = useNavigate()

    const { '*': splat } = useParams()
    const list = splat?.split('/')
    const lastId = list?.pop() || ''

    const saveAsset = async () => {
        setSavingAsset(true)
        const createDefaultAsset = (type: ValueObjectType<typeof ASSET_TYPE>): SendAssetType => ({
            name: assetName,
            id: uuid(),
            parent: lastId,
            type: type,
            url: '',
            preview: '',
            props: {},
            patch: {},
            tags: []
        })
        const onSuccess = (): string => {
            setSavingAsset(false)
            navigate(`/assets/${splat}`, { replace: true })
            return 'Asset successfully saved!'
        }

        const onFail = (e?: any): string => {
            console.error(e)
            setSavingAsset(false)
            return 'Could not save asset'
        }

        if (selectedType === ASSET_TYPE.Folder) {
            const assetFolder = createDefaultAsset(ASSET_TYPE.Folder)
            createAsset({ assets: [assetFolder] })
                .then(onSuccess)
                .catch(onFail)

            return
        }
        if (selectedType === ASSET_TYPE.Scene) {
            const assetFolder = createDefaultAsset(ASSET_TYPE.Scene)
            createAsset({ assets: [assetFolder] })
                .then(onSuccess)
                .catch(onFail)

            return
        }

        const nameWithExtension = `${assetName}${assetExt}`
        const fileToUpload = Utils.renameFile(selectedFile as File, nameWithExtension)

        try {
            await toast.promise(
                Utils.createAssetsFromFile(fileToUpload, lastId).then(res => createAsset({ assets: res })),
                {
                    loading: 'Saving asset...',
                    success: onSuccess,
                    error: onFail
                }
            )
        } catch (e) {
            onFail(e)
        }
    }

    const processSelection = (type: TSelection) => {
        if (type === 'folder') {
            setSelectedType(type)
            return
        }

        setSelectedType('')

        const mimetype = FILE_SELECTOR_TYPE[type]

        fileSelector.current!.accept = mimetype
        fileSelector.current!.click()
    }
    const createScene = () => {
        setSelectedType(ASSET_TYPE.Scene)
    }
    const selectFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.stopPropagation()
        e.preventDefault()

        if (!e.target.files?.length) {
            return
        }

        const file = e.target.files![0] as File
        setSelectedFile(file)

        const name = file.name.replace(extRegexp, '')
        setAssetName(name)

        const ext = file.name.match(extRegexp)!.pop() as string
        setAssetExt(ext)
    }

    return (
        <PageBuilder breadcrumbs={['assets', 'create']} documentTitle="Create Asset" title="Create Asset">
            <Typography.Text>Name</Typography.Text>
            <Input
                placeholder="Asset name"
                value={assetName}
                onChange={e => {
                    setAssetName(e.target.value)
                }}></Input>
            <p></p>
            <Typography.Text>Type</Typography.Text>
            <div>
                <div>
                    <Button
                        onClick={() => processSelection('folder')}
                        style={buttonWidthStyle}
                        type={selectedType === 'folder' ? 'primary' : 'default'}
                        icon={<FolderOutlined />}>
                        Folder
                    </Button>
                </div>
                <div style={{ marginTop: 16 }}>
                    <Space size={[16, 0]}>
                        <Button
                            onClick={createScene}
                            style={buttonWidthStyle}
                            type={selectedType === 'scene' ? 'primary' : 'default'}
                            icon={<SmileOutlined />}>
                            Scene
                        </Button>
                        <Button
                            onClick={() => processSelection('model')}
                            style={buttonWidthStyle}
                            icon={<SmileOutlined />}>
                            Model
                        </Button>
                    </Space>
                </div>
                <div style={{ marginTop: 16 }}>
                    <Space size={[16, 0]}>
                        <Button
                            onClick={() => processSelection('material')}
                            style={buttonWidthStyle}
                            // icon={}
                        >
                            Physical material
                        </Button>
                        <Button
                            style={buttonWidthStyle}
                            onClick={() => processSelection('image')}
                            icon={<PictureOutlined />}>
                            Image
                        </Button>
                        <Button onClick={() => processSelection('vector')} style={buttonWidthStyle} disabled>
                            Vector
                        </Button>
                        <Button onClick={() => processSelection('composite')} style={buttonWidthStyle} disabled>
                            Composite
                        </Button>
                    </Space>
                </div>
            </div>
            <p></p>
            <Button
                type="primary"
                onClick={saveAsset}
                disabled={selectedType === '' ? !selectedFile : false}
                loading={savingAsset}>
                Save asset
            </Button>
            <input ref={fileSelector} type="file" style={{ display: 'none' }} onChange={selectFile}></input>
        </PageBuilder>
    )
}

export default CreateAsset
